import axios from 'axios';
import { DHLError, axiosRequest, getCurrentDate, addMinutesToDateTime } from '@general/js/utils';
import { URLS, CONFIG } from '../constants/API';

export default class Services {
  /**
   * Gets the members in the policy
   * @param {string} memberNumber
   * @param {array} optionalParams optional member query parameters
   * @returns {object}
   */
  static async getMembershipMembers(memberNumber, optionalParams) {
    try {
      const memberQueryParams = optionalParams ? `firstname,lastname,${optionalParams.join(',')}` : 'firstname,lastname';
      const response = await axios.get(
        decodeURI(
          `${URLS.GET_MEMBERSHIP_MEMBERS}?$select=dhl_latestmemberstatus,dhl_memberid,dhl_membershipid,dhl_membershipmembersid&$expand=dhl_Member($select=${memberQueryParams}),dhl_relationship($select=dhl_name),dhl_InsurableType($select=dhl_code,dhl_name)&$filter=dhl_membershipid eq '${memberNumber}'`
        )
      );

      if (response.data && response.data.value) {
        return response.data.value;
      }

      throw new Error(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`getMembershipMembers() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get membership policies using the membership policy GUID
   * @param {string} memberPolicy - memberPolicy GUID
   * @returns {object}
   */
  static async getMembershipPolicies(memberPolicy) {
    try {
      const url = `${URLS.GET_MEMBERSHIP_POLICIES}(${memberPolicy})/dhl_dhl_membershippolicy_product?$select=name,productnumber,producttypecode,validfromdate,validtodate,statecode,statuscode&$expand=dhl_product_dhl_productexcess_product($select=dhl_excess,dhl_startdate,dhl_enddate,dhl_composition,dhl_eligibilitysegment,statecode,statuscode,_dhl_product_value)`;

      const response = await axiosRequest().get(url, { ...CONFIG });
      if (response.data && response.data.value) {
        return response.data.value;
      }

      throw new DHLError(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`getMembershipPolicies() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Gets the migration date from CRM config
   * @returns {object}
   */
  static async getMigrationDate() {
    try {
      const url = `${URLS.GET_INTERNAL_CONFIGURATIONS}?$select=dhl_name,dhl_value&$filter=dhl_name eq 'MigrationDate'`;

      const response = await axios.get(decodeURI(url));
      if (response.data && response.data.value) {
        return response.data.value[0];
      }

      throw new DHLError(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`getMigrationDate() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /** 
   * @deprecated Since Bambora has been replaced by Ipsi.
   * Get bambora credentials from CRM config
   * @returns {object}
   */
  static async getBamboraCredentials() {
    try {
      const response = await axios.get(
        decodeURI(
          `${URLS.GET_INTERNAL_CONFIGURATIONS}?$select=dhl_name,dhl_value,dhl_clientid,dhl_clientsecret&$filter=dhl_name eq 'Bambora API' or dhl_name eq 'DHLBombaraToken' or dhl_name eq 'BamboraURL'`
        )
      );

      if (response.data && response.data.value) {
        return response.data.value;
      }

      throw new Error(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`getBamboraCredentials() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get IPSI iFrame Urls from CRM config
   * @returns {object}
   */
  static async getIpsiUrls() {
    try {
      const response = await axios.get(
        decodeURI(
          `${URLS.GET_INTERNAL_CONFIGURATIONS}?$select=dhl_name,dhl_value&$filter=dhl_name eq 'IpsiLongTermTokenURL' or dhl_name eq 'IpsiShortTermTokenURL'`
        )
      );

      const returnResponse = {};

      response.data.value.forEach((item) => {
        if (item.dhl_name === 'IpsiLongTermTokenURL') {
          returnResponse.longTermTokenUrl = item.dhl_value;
        } else if (item.dhl_name === 'IpsiShortTermTokenURL') {
          returnResponse.shortTermTokenUrl = item.dhl_value;
        }
      });
      if (returnResponse.longTermTokenUrl && returnResponse.shortTermTokenUrl) {
        return returnResponse;
      }
      throw new Error(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`getIpsiUrls() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /** 
   * @deprecated Since Bambora has been replaced by Ipsi.
   * Get bambora token for recurring credit card payments
   * @param {string} orderId - Membership Policy Id used as the orderId
   * @param {string} sttToken - Token required to make the request
   * @returns {string}
   */
  static async getBamboraToken(orderId, sttToken) {
    try {
      const res = await axios.post(URLS.GET_BAMBORA_TOKEN, { orderId, sttToken });

      if (res.data && res.data.response) {
        const obj = JSON.parse(res.data.response);
        return obj.Token;
      }
      throw new DHLError(`Issue with the response: ${res}`);
    } catch (error) {
      throw new DHLError(`getBamboraToken() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Upload rebate form to CRM blob storage
   * @param {string} memberNumber - Current policy member number
   * @param {string} fileName - File source identifier (from REBATE_FORM_PDF_FILENAMES enum)
   * @param {string} b64EncodedFile - Base 64 encoded file string
   * @returns {string}
   */
  static async uploadRebateFormPdfCRM(memberNumber, fileName, b64EncodedFile) {
    try {
      const ddmmyyyy = getCurrentDate().split('-').reverse().join('');

      const response = await axiosRequest().post(URLS.SAVE_REBATE_FORM, {
        inputPayload: JSON.stringify({
          memberNumber,
          currentDate: getCurrentDate(),
          fileName: `${fileName}-${ddmmyyyy}`,
          fileBody: b64EncodedFile
        })
      });

      if (response.data && response.data.responseString) {
        return response.data.responseString;
      }

      throw new Error(`Issue with the response: ${response}`);

    } catch (error) {
      throw new DHLError(`uploadRebateFormPdfCRM() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

    /**
   * Function to raise a work item for submit policy flow in CRM.
   *
   * @param {string} memberNumber Member number
   * @param {object} payload Failed payload when submit policy flow fails 
   * @param {string} functionName API message name that failed
   */
    static async raiseWorkItemCRM(memberNumber, payload, functionName) {
    try {

      const currentDateTimePlus30Mins = addMinutesToDateTime(new Date(), 30);

      const response = await axiosRequest().post(URLS.CREATE_WORK_ITEM, { 
        inputPayload: JSON.stringify({
          memberNumber,
          regardingObjectIdType: 'MEMBERSHIP',
          regardingMemberNumber: memberNumber,
          activityType: 'task',
          taskType: 'FOLLOW_UP',
          category:'MEMBER_CARE',
          subCategory: 'ENQUIRY',
          subsubCategory: 'NA',
          title: `Submission Timeout for ${memberNumber} in Consultant Portal`,
          dueDate: currentDateTimePlus30Mins,
          functionName,
          payload,
        })
      });

      if (response.data && response.data.responseString) {
        return response.data.responseString;
      }

      throw new Error(`Issue with the response: ${response}`);
      
    } catch (error) {
      throw new DHLError(`raiseWorkItemCRM() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }



  /**
   * Retrieves the premium adjustment prices and dates for the Premium Adjustment timeline
   * @param {string} memberNumber member number or policy number
   * @param {boolean} showPremiumPaymentAmount whether the premium payment amount is to be included in response
   * @param {string} currentDate current date in 'YYYY-MM-DD'
   * @returns
   */
  static async getPremiumAdjustmentPricesCRM(memberNumber, showPremiumPaymentAmount, currentDate) {
    try {
      const response = await axiosRequest().post(URLS.GET_PREMIUM_ADJUSTMENTS, { memberNumber, showPremiumPaymentAmount, currentDate });

      if (response.data && response.data.responseString) {
        return JSON.parse(response.data.responseString);
      }

      throw new Error(`Issue with the response: ${response}`);
    } catch (error) {
      throw new Error(`getPremiumAdjustmentPrices() - Call failed with error`);
    }
  }
}